import { Type, type Static } from '@sinclair/typebox';
import {
  BookingPromoIneligibilityReason,
  ProPromoCodeKind,
  ProPromoCodeTrigger,
} from './translation_namespace/pro_promo_code_namespace';
import {
  ReservationAmountDiscountSource,
  ReservationDiscountReason,
  ReservationDiscountUnavailableReason,
} from './translation_namespace/reservation_discount_namespace';

/* Remise posée par le pro sur un rendez-vous depuis l'agenda (spec Promotions § 6.4, lot 1). Montants en
   centimes ; les chaînes de prix sont formatées par le backend, signe compris. */

/** POST /app/reservation/:reservation_id/discount */
export const CreateReservationDiscountRequestSchema = Type.Object({
  discount_kind: Type.Enum(ProPromoCodeKind),
  /** Taux de 1 à 100 (`PERCENT`) ou montant en centimes (`FIXED_AMOUNT_MINOR`). */
  discount_value: Type.Integer({ minimum: 1 }),
  reason: Type.Optional(Type.Enum(ReservationDiscountReason)),
  /** Précision libre du motif (« Autre »). */
  reason_note: Type.Optional(Type.String({ maxLength: 200 })),
});
export type CreateReservationDiscountRequestObject = Static<typeof CreateReservationDiscountRequestSchema>;

/** Ligne de prestation du bloc Montant, au prix normal. */
export const ReservationAmountsLineSchema = Type.Object({
  label: Type.String(),
  /** Formule, durée, suppléments. */
  detail: Type.Optional(Type.String()),
  /** Absent quand le prix n'est pas ferme (sur devis, « à partir de »). */
  amount: Type.Optional(Type.String()),
  amount_raw: Type.Optional(Type.Integer()),
});
export type ReservationAmountsLineObject = Static<typeof ReservationAmountsLineSchema>;

/** Ligne de remise (ou de majoration) du bloc Montant. */
export const ReservationAmountsDiscountSchema = Type.Object({
  source: Type.Enum(ReservationAmountDiscountSource),
  /** Remise posée par le pro : sa suppression passe par cet identifiant. */
  reservation_discount_id: Type.Optional(Type.Integer()),
  /** Code promo du client (`PROMO_CODE`) : code et libellé de l'offre. */
  promo_code: Type.Optional(Type.String()),
  promo_label: Type.Optional(Type.String()),
  /** Remise du pro : nature et valeur saisies (taux en %, ou centimes), motif. */
  discount_kind: Type.Optional(Type.Enum(ProPromoCodeKind)),
  discount_value: Type.Optional(Type.Integer()),
  reason: Type.Optional(Type.Enum(ReservationDiscountReason)),
  reason_note: Type.Optional(Type.String()),
  created_by_name: Type.Optional(Type.String()),
  // ISO UTC brut, sans `format` : le détail du rendez-vous retransforme ce bloc, une date déjà convertie
  // dans le fuseau du pro ne se reformaterait plus (`created_at_formatted` vide).
  created_at: Type.Optional(Type.String()),
  created_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'created_at', is_short: true } }),
  ),
  /** Montant formaté et signé : « -10,00 € » (remise), « +10,00 € » (majoration). */
  amount: Type.String(),
  /** Montant en centimes, toujours positif ; `is_surcharge` dit le sens d'un ajustement. */
  amount_raw: Type.Integer(),
  is_surcharge: Type.Optional(Type.Boolean()),
});
export type ReservationAmountsDiscountObject = Static<typeof ReservationAmountsDiscountSchema>;

/** Bloc Montant d'un rendez-vous : lignes au prix normal, remises, total après remise. */
export const ReservationAmountsSchema = Type.Object({
  reservation_id: Type.Integer(),
  // Liste vide renvoyée `null` (transformToSchema) : à lire avec `?? []`.
  lines: Type.Optional(Type.Array(ReservationAmountsLineSchema)),
  discounts: Type.Optional(Type.Array(ReservationAmountsDiscountSchema)),
  /** Total au prix normal (lignes), ou prix final fixé par le pro ; absent tant que le prix n'est pas fixé. */
  gross_total: Type.Optional(Type.String()),
  gross_total_raw: Type.Optional(Type.Integer()),
  /** Total enregistré de la réservation, après remises ; absent s'il est inconnu. */
  net_total: Type.Optional(Type.String()),
  net_total_raw: Type.Optional(Type.Integer()),
  /** Prix normal − total enregistré, signé « - » (tuile Prix) ; absent sans remise. */
  discount_total: Type.Optional(Type.String()),
  discount_total_raw: Type.Optional(Type.Integer()),
  /**
   * Le total ne se calcule pas sur les lignes (prix sur devis, « à partir de », indéterminé, ou aucune ligne) :
   * le pro fixe le prix final (champ « Prix final » du formulaire d'édition, envoyé en `total_price_override`).
   */
  has_unpriced_lines: Type.Optional(Type.Boolean()),
  /** Prix final fixé par le pro (lignes sans prix ferme) : base des remises, pré-remplit le champ « Prix final ». */
  final_total: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'final_total_raw' } })),
  final_total_raw: Type.Optional(Type.Integer()),
  /** Une remise peut être posée (indépendamment des droits du membre, vérifiés par l'app). */
  can_add_discount: Type.Boolean(),
  discount_unavailable_reason: Type.Optional(Type.Enum(ReservationDiscountUnavailableReason)),
  /** Le montant saisi à la main (ligne `ADJUSTMENT`) peut être retiré : le total revient au calcul des lignes. */
  can_remove_adjustment: Type.Boolean(),
});
export type ReservationAmountsObject = Static<typeof ReservationAmountsSchema>;

/**
 * POST /app/reservation/:reservation_id/discount/preview — effet d'une remise avant de la poser (popover de
 * l'agenda). Une remise trop grande ou hors bornes n'est pas une erreur HTTP : `error` porte sa clé.
 */
export const ReservationDiscountPreviewSchema = Type.Object({
  /** Total enregistré avant la remise. */
  net_total_before: Type.String(),
  net_total_before_raw: Type.Integer(),
  /** Ce qui reste à remiser (plafond d'une remise en montant). */
  max_discount: Type.String(),
  max_discount_raw: Type.Integer(),
  /** Remise retenue, signée « - », et total après remise ; absents si elle ne peut pas se poser. */
  discount_amount: Type.Optional(Type.String()),
  discount_amount_raw: Type.Optional(Type.Integer()),
  net_total_after: Type.Optional(Type.String()),
  net_total_after_raw: Type.Optional(Type.Integer()),
  /** Clé d'erreur de l'API (`RESERVATION_DISCOUNT_EXCEEDS_TOTAL`, `RESERVATION_DISCOUNT_INVALID_VALUE`). */
  error: Type.Optional(Type.String()),
});
export type ReservationDiscountPreviewObject = Static<typeof ReservationDiscountPreviewSchema>;

// ─── Promotion posée depuis l'agenda (lot 2) ────────────────────────────────

/** Une promotion proposée sur le rendez-vous (onglet « Promotion » du popover « Ajouter une remise »). */
export const ReservationEligiblePromotionSchema = Type.Object({
  pro_promo_code_id: Type.Integer(),
  label: Type.String(),
  code: Type.Optional(Type.String()),
  trigger: Type.Enum(ProPromoCodeTrigger),
  /** « −20 % », « −10,00 € ». */
  value_label: Type.String(),
  /** Fin de validité (« jusqu'au 30 nov. »), absente sans fin. */
  valid_until: Type.Optional(Type.String({ format: 'date-time' })),
  valid_until_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'valid_until', is_short: true } }),
  ),
  eligible: Type.Boolean(),
  /** Promotion grisée : pourquoi elle ne s'applique pas à ce rendez-vous. */
  reason: Type.Optional(Type.Enum(BookingPromoIneligibilityReason)),
  /** Promotion applicable : remise et total après remise, formatés. */
  discount_amount: Type.Optional(Type.String()),
  discount_amount_raw: Type.Optional(Type.Integer()),
  net_total_after: Type.Optional(Type.String()),
  /** La promotion automatique la plus avantageuse : présélectionnée. */
  is_suggested: Type.Optional(Type.Boolean()),
});
export type ReservationEligiblePromotionObject = Static<typeof ReservationEligiblePromotionSchema>;

/** GET /app/reservation/:reservation_id/eligible-promotions */
export const ReservationEligiblePromotionsResponseSchema = Type.Object({
  reservation_id: Type.Integer(),
  // Liste vide renvoyée `null` (transformToSchema) : à lire avec `?? []`.
  items: Type.Optional(Type.Array(ReservationEligiblePromotionSchema)),
  /** Aucune remise possible sur ce rendez-vous (annulé, course, série, prix pas ferme…). */
  unavailable_reason: Type.Optional(Type.Enum(ReservationDiscountUnavailableReason)),
  /** Une promotion est déjà appliquée (une seule par réservation) : son libellé. */
  promotion_applied_label: Type.Optional(Type.String()),
  /** Total actuel, d'où part chaque « nouveau total » (« au lieu de 90,00 € »). */
  net_total_before: Type.Optional(Type.String()),
});
export type ReservationEligiblePromotionsResponseObject = Static<typeof ReservationEligiblePromotionsResponseSchema>;

/** POST /app/reservation/:reservation_id/promotion */
export const ApplyReservationPromotionRequestSchema = Type.Object({
  pro_promo_code_id: Type.Integer({ minimum: 1 }),
});
export type ApplyReservationPromotionRequestObject = Static<typeof ApplyReservationPromotionRequestSchema>;

// ─── Déplacement d'un rendez-vous remisé par le pro (lot 4) ─────────────────

/** Condition de la promotion que le nouvel horaire ne remplit plus. */
export enum ReservationMoveOutsideReason {
  /** Hors des heures (happy hours). */
  HOURS = 'HOURS',
  /** Hors des dates, quand elles bornent le rendez-vous. */
  DATES = 'DATES',
}

/** POST /app/reservation/:reservation_id/move-check — avant de valider un déplacement depuis l'agenda. */
export const ReservationMoveCheckRequestSchema = Type.Object({
  /** Nouveaux débuts des rendez-vous déplacés : heure du pro (sans fuseau) ou instant ISO. */
  starts: Type.Array(Type.String({ format: 'date-time' }), { minItems: 1, maxItems: 50 }),
  /**
   * Rendez-vous de chaque début (même ordre, même nombre) : seuls les rendez-vous remisés sont jugés. Absent :
   * autant de débuts que de rendez-vous est le report de toute la réservation (débuts dans l'ordre de ses
   * rendez-vous, comme `POST /reschedule`) ; sinon chaque début est jugé quel que soit son rendez-vous.
   */
  appointment_ids: Type.Optional(Type.Array(Type.Integer({ minimum: 1 }), { minItems: 1, maxItems: 50 })),
});
export type ReservationMoveCheckRequestObject = Static<typeof ReservationMoveCheckRequestSchema>;

/**
 * Le nouvel horaire sort-il des conditions de la promotion de la réservation ? La remise est conservée (à
 * prestations identiques, le prix ne change pas) : le pro est seulement averti. Une remise libre n'a pas de
 * conditions. Libellés formatés par le serveur (locale de la requête, fuseau du pro).
 */
export const ReservationMoveCheckResponseSchema = Type.Object({
  outside_conditions: Type.Boolean(),
  reason: Type.Optional(Type.Enum(ReservationMoveOutsideReason)),
  /** Libellé public de la promotion (« Happy hours »). */
  promotion_name: Type.Optional(Type.String()),
  /** Premier nouvel horaire hors conditions : « vendredi 30 oct. » et « 15:00 ». */
  start_date_label: Type.Optional(Type.String()),
  start_time_label: Type.Optional(Type.String()),
  /** Remise de la promotion, conservée (« 6,75 € »). */
  discount_amount: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'discount_amount_minor' } })),
});
export type ReservationMoveCheckResponseObject = Static<typeof ReservationMoveCheckResponseSchema>;
